import React, { useRef, useState, useEffect } from 'react';
import { MapPin, ArrowLeft, Info } from 'lucide-react';
import * as THREE from 'three';

// Mars panorama locations (using NASA PIA panorama IDs via /api/mars-images)
const MARS_LOCATIONS = [
  {
    id: 1,
    name: "Jezero Crater - Perseverance",
    lat: 18.4,
    lon: 77.5,
    panorama: "/api/mars-images?id=PIA24264",
    description: "Perseverance's first panorama of Jezero Crater",
    rover: "Perseverance"
  },
  {
    id: 2,
    name: "Falbreen Panorama",
    lat: 18.5,
    lon: 77.6,
    panorama: "/api/mars-images?id=PIA26644",
    description: "360-degree Mastcam-Z view at Falbreen",
    rover: "Perseverance"
  },
  {
    id: 3,
    name: "Vera Rubin Ridge",
    lat: -4.7,
    lon: 137.3,
    panorama: "/api/mars-images?id=PIA22486",
    description: "Curiosity panorama from Vera Rubin Ridge",
    rover: "Curiosity"
  },
  {
    id: 4,
    name: "Pahrump Hills",
    lat: -4.65,
    lon: 137.38,
    panorama: "/api/mars-images?id=PIA19808",
    description: "Curiosity panorama from the base of Mount Sharp",
    rover: "Curiosity"
  },
  {
    id: 5,
    name: "Rocknest",
    lat: -4.59,
    lon: 137.44,
    panorama: "/api/mars-images?id=PIA16800",
    description: "Curiosity’s early panorama from Rocknest",
    rover: "Curiosity"
  }
];

// Convert lat/lon to 3D position on sphere
const latLonToVector3 = (lat, lon, radius = 1) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
};

// Mars Globe Component
const MarsGlobe = ({ onLocationClick, selectedLocationId }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const globeRef = useRef(null);
  const markersRef = useRef([]);
  const raycasterRef = useRef(null);
  const mouseRef = useRef(new THREE.Vector2());
  const isDragging = useRef(false);
  const previousMousePosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 3;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    // Raycaster
    const raycaster = new THREE.Raycaster();
    raycasterRef.current = raycaster;

    // Mars globe
    const geometry = new THREE.SphereGeometry(1, 64, 64);
    const textureLoader = new THREE.TextureLoader();
    const marsTexture = textureLoader.load('/mars_texture.jpg');
    const material = new THREE.MeshPhongMaterial({ map: marsTexture, bumpScale: 0.05 });

    const globe = new THREE.Mesh(geometry, material);
    globe.rotation.y = Math.PI;
    scene.add(globe);
    globeRef.current = globe;

    // Markers
    const markerGeometry = new THREE.SphereGeometry(0.02, 16, 16);
    MARS_LOCATIONS.forEach((location) => {
      const markerMaterial = new THREE.MeshBasicMaterial({ color: 0xff6b6b });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      const position = latLonToVector3(location.lat, location.lon, 1.02);
      marker.position.copy(position);
      marker.userData = { location };
      scene.add(marker);
      markersRef.current.push(marker);
      marker.userData.pulsePhase = Math.random() * Math.PI * 2;
    });

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (!isDragging.current) globe.rotation.y += 0.001;

      // Animate markers
      markersRef.current.forEach((marker) => {
        marker.userData.pulsePhase += 0.05;
        const scale = 1 + Math.sin(marker.userData.pulsePhase) * 0.3;
        marker.scale.set(scale, scale, scale);
      });

      renderer.render(scene, camera);
    };
    animate();

    // Mouse events
    const onMouseDown = (e) => {
      isDragging.current = true;
      previousMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseMove = (e) => {
      if (isDragging.current) {
        const deltaX = e.clientX - previousMousePosition.current.x;
        const deltaY = e.clientY - previousMousePosition.current.y;
        globe.rotation.y += deltaX * 0.005;
        globe.rotation.x += deltaY * 0.005;
        globe.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, globe.rotation.x));
        previousMousePosition.current = { x: e.clientX, y: e.clientY };
      } else {
        const rect = container.getBoundingClientRect();
        mouseRef.current.x = ((e.clientX - rect.left) / width) * 2 - 1;
        mouseRef.current.y = -((e.clientY - rect.top) / height) * 2 + 1;
        raycaster.setFromCamera(mouseRef.current, camera);
        const intersects = raycaster.intersectObjects(markersRef.current);
        renderer.domElement.style.cursor = intersects.length > 0 ? 'pointer' : 'grab';
      }
    };

    const onMouseUp = () => {
      if (!isDragging.current) {
        raycaster.setFromCamera(mouseRef.current, camera);
        const intersects = raycaster.intersectObjects(markersRef.current);
        if (intersects.length > 0) {
          onLocationClick(intersects[0].object.userData.location);
        }
      }
      isDragging.current = false;
    };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, [onLocationClick]);

  return <div ref={mountRef} className="w-full h-full" />;
};

// Panorama Viewer Component
const PanoramaViewer = ({ location, onBack }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    (async () => {
      const res = await fetch(location.panorama);
      const { image } = await res.json();
      const texture = new THREE.TextureLoader().load(image, () => renderer.render(scene, camera));
      const sphere = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ map: texture }));
      scene.add(sphere);
    })();

    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => renderer.dispose();
  }, [location]);

  return (
    <div className="relative w-full h-full">
      <div ref={mountRef} className="w-full h-full" />

      <button
        onClick={onBack}
        className="absolute top-4 left-4 z-30 bg-black/70 hover:bg-black/90 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Globe
      </button>

      <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-md rounded-lg p-4 text-white">
        <div className="flex items-start gap-3">
          <MapPin className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-xl mb-1">{location.name}</h3>
            <p className="text-sm text-gray-300 mb-2">{location.description}</p>
            <div className="text-xs text-gray-400">
              Rover: {location.rover} • Drag to look around
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Location List Component
const LocationList = ({ locations, onSelect, selectedId }) => {
  return (
    <div className="absolute top-24 right-4 bg-black/80 backdrop-blur-md rounded-lg p-4 max-w-xs">
      <div className="flex items-center gap-2 mb-3">
        <Info className="w-5 h-5 text-red-400" />
        <h3 className="font-bold text-white">Available Locations</h3>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {locations.map((location) => (
          <button
            key={location.id}
            onClick={() => onSelect(location)}
            className={`w-full text-left p-3 rounded-lg transition-colors ${
              selectedId === location.id
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 hover:bg-gray-700 text-gray-200'
            }`}
          >
            <div className="font-medium text-sm mb-1">{location.name}</div>
            <div className="text-xs opacity-75">{location.rover}</div>
          </button>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-700">
        <p className="text-xs text-gray-400">
          Click any location or red marker on the globe to explore
        </p>
      </div>
    </div>
  );
};

// Main App Component
export default function MarsExplorer() {
  const [viewMode, setViewMode] = useState('globe');
  const [selectedLocation, setSelectedLocation] = useState(null);

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    setViewMode('panorama');
  };

  const handleBackToGlobe = () => {
    setViewMode('globe');
  };

  return (
    <div className="w-screen h-screen bg-black overflow-hidden relative">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/90 via-black/50 to-transparent p-6 pointer-events-none">
        <h1 className="text-4xl font-bold text-white mb-2">Mars Street View</h1>
        <p className="text-gray-300 text-sm">
          {viewMode === 'globe' 
            ? 'Explore real 360° panoramas from NASA Mars rovers' 
            : 'Immersive panorama view - drag to look around'}
        </p>
      </div>

      {/* Main View */}
      <div className="w-full h-full">
        {viewMode === 'globe' ? (
          <>
            <div className="w-full h-full bg-gradient-to-br from-red-900/10 to-orange-900/10">
              <MarsGlobe 
                onLocationClick={handleLocationClick}
                selectedLocationId={selectedLocation?.id}
              />
            </div>
            
            {/* Controls info */}
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-md rounded-lg p-3 text-white text-xs max-w-md">
              <p className="font-semibold mb-1">Controls:</p>
              <p>🖱️ Drag to rotate globe • 🖱️ Scroll to zoom • 📍 Click red markers to enter Street View</p>
            </div>

            {/* Location List */}
            <LocationList 
              locations={MARS_LOCATIONS}
              onSelect={handleLocationClick}
              selectedId={selectedLocation?.id}
            />
          </>
        ) : (
          <PanoramaViewer 
            location={selectedLocation} 
            onBack={handleBackToGlobe} 
          />
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-center pointer-events-none">
        <p className="text-gray-500 text-xs">
          Panoramas and textures from NASA • {MARS_LOCATIONS.length} locations available
        </p>
      </div>
    </div>
  );
}